import { supabase } from '../lib/supabase';

// Messaging service for real-time conversations
export const messagingService = {
  // Get user conversations
  async getUserConversations(userId) {
    try {
      const { data, error } = await supabase
        ?.from('conversations')
        ?.select(`
          id,
          subject,
          status,
          last_message_at,
          created_at,
          client:client_id (
            id,
            full_name,
            avatar_url
          ),
          practitioner:practitioner_id (
            id,
            full_name,
            avatar_url
          ),
          messages (
            id,
            content,
            sender_id,
            status,
            created_at
          )
        `)
        ?.or(`client_id.eq.${userId},practitioner_id.eq.${userId}`)
        ?.eq('status', 'active')
        ?.order('last_message_at', { ascending: false });

      if (error) {
        return { data: [], error: error?.message };
      }

      // Transform conversations to include latest message and other participant info
      const transformedData = data?.map(conversation => {
        const isClient = conversation?.client?.id === userId;
        const otherParticipant = isClient ? conversation?.practitioner : conversation?.client;
        const latestMessage = conversation?.messages?.[0] || null;

        return {
          id: conversation?.id,
          subject: conversation?.subject,
          status: conversation?.status,
          last_message_at: conversation?.last_message_at,
          created_at: conversation?.created_at,
          other_participant: otherParticipant,
          latest_message: latestMessage,
          is_client: isClient,
          unread_count: conversation?.messages?.filter(msg => 
            msg?.sender_id !== userId && msg?.status !== 'read'
          )?.length || 0
        };
      }) || [];

      return { data: transformedData, error: null };
    } catch (error) {
      const errorMessage = error?.message?.includes('Failed to fetch')
        ? 'Cannot connect to database. Your Supabase project may be paused or deleted. Please visit your Supabase dashboard to check project status.' :'Failed to load conversations. Please try again.';
      
      return { data: [], error: errorMessage };
    }
  },

  // Get conversation messages
  async getConversationMessages(conversationId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('messages')
        ?.select(`
          id,
          content,
          sender_id,
          status,
          read_at,
          created_at,
          updated_at,
          sender:sender_id (
            id,
            full_name,
            avatar_url
          )
        `)
        ?.eq('conversation_id', conversationId)
        ?.order('created_at', { ascending: true });

      if (error) {
        return { data: [], error: error?.message };
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: 'Failed to load messages.' };
    }
  },

  // Create new conversation
  async createConversation(clientId, practitionerId, subject) {
    try {
      // Check if conversation already exists
      const { data: existingConversation } = await supabase
        ?.from('conversations')
        ?.select('id')
        ?.eq('client_id', clientId)
        ?.eq('practitioner_id', practitionerId)
        ?.eq('subject', subject)
        ?.single();

      if (existingConversation) {
        return { data: existingConversation, error: null };
      }

      // Create new conversation
      const { data, error } = await supabase
        ?.from('conversations')
        ?.insert({
          client_id: clientId,
          practitioner_id: practitionerId,
          subject,
          status: 'active',
          created_at: new Date()?.toISOString(),
          last_message_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to create conversation.' };
    }
  },

  // Send message
  async sendMessage(conversationId, senderId, content) {
    try {
      const { data, error } = await supabase
        ?.from('messages')
        ?.insert({
          conversation_id: conversationId,
          sender_id: senderId,
          content: content?.trim(),
          status: 'sent',
          created_at: new Date()?.toISOString()
        })
        ?.select(`
          id,
          content,
          sender_id,
          status,
          created_at,
          sender:sender_id (
            id,
            full_name,
            avatar_url
          )
        `)
        ?.single();

      if (error) {
        return { data: null, error: error?.message };
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to send message.' };
    }
  },

  // Mark message as read
  async markMessageAsRead(messageId) {
    try {
      const { error } = await supabase
        ?.from('messages')
        ?.update({
          status: 'read',
          read_at: new Date()?.toISOString()
        })
        ?.eq('id', messageId);

      if (error) {
        return { error: error?.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'Failed to mark message as read.' };
    }
  },

  // Mark all conversation messages as read
  async markConversationAsRead(conversationId, userId) {
    try {
      const { error } = await supabase
        ?.from('messages')
        ?.update({
          status: 'read',
          read_at: new Date()?.toISOString()
        })
        ?.eq('conversation_id', conversationId)
        ?.neq('sender_id', userId)
        ?.eq('status', 'sent');

      if (error) {
        return { error: error?.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'Failed to mark conversation as read.' };
    }
  },

  // Subscribe to conversation messages (real-time)
  subscribeToMessages(conversationId, callback) {
    try {
      const subscription = supabase
        ?.channel(`conversation-${conversationId}`)
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            callback?.(payload);
          }
        )
        ?.subscribe();

      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to messages:', error);
      return null;
    }
  },

  // Unsubscribe from real-time updates
  unsubscribeFromMessages(subscription) {
    try {
      if (subscription) {
        supabase?.removeChannel(subscription);
      }
    } catch (error) {
      console.error('Failed to unsubscribe from messages:', error);
    }
  },

  // Update conversation status
  async updateConversationStatus(conversationId, status) {
    try {
      const { error } = await supabase
        ?.from('conversations')
        ?.update({
          status,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', conversationId);

      if (error) {
        return { error: error?.message };
      }

      return { error: null };
    } catch (error) {
      return { error: 'Failed to update conversation status.' };
    }
  },

  // Get unread message count for user
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase
        ?.from('messages')
        ?.select('*', { count: 'exact', head: true })
        ?.neq('sender_id', userId)
        ?.eq('status', 'sent')
        ?.in('conversation_id', 
          supabase
            ?.from('conversations')
            ?.select('id')
            ?.or(`client_id.eq.${userId},practitioner_id.eq.${userId}`)
        );

      if (error) {
        return { count: 0, error: error?.message };
      }

      return { count: count || 0, error: null };
    } catch (error) {
      return { count: 0, error: 'Failed to get unread count.' };
    }
  }
};

export default messagingService;